import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

export function useWidgetFichaje(onEstadoCambiado) {
    const { showToast } = useToast();
    const [fichaje, setFichaje] = useState(null);
    const [segundos, setSegundos] = useState(0);
    const [procesando, setProcesando] = useState(false);
    const intervalRef = useRef(null);
    const ultimaAlertaRef = useRef(null); // evita repetir el mismo aviso de jornada

    const fetchEstado = useCallback(async () => {
        try {
            const res = await api.get('/fichajes/estado');
            const data = res.data || res;
            if (data?.activo && data?.datos) {
                setFichaje(data.datos);
                const inicio = new Date(data.datos.entrada).getTime();
                setSegundos(Math.floor((new Date().getTime() - inicio) / 1000));
                if (onEstadoCambiado) onEstadoCambiado(true);
            } else {
                setFichaje(null);
                setSegundos(0);
                if (onEstadoCambiado) onEstadoCambiado(false);
            }

            // ⚖️ Aviso de jornada (próxima/excedida): se muestra una sola vez por tipo
            if (data?.alerta && data.alerta.tipo !== ultimaAlertaRef.current) {
                ultimaAlertaRef.current = data.alerta.tipo;
                showToast(data.alerta.mensaje, data.alerta.tipo === 'JORNADA_EXCEDIDA' ? 'error' : 'warning');
            } else if (!data?.alerta) {
                ultimaAlertaRef.current = null;
            }
        } catch (error) {
            console.error("Error al obtener estado:", error);
        }
    }, [onEstadoCambiado, showToast]);

    useEffect(() => { fetchEstado(); }, [fetchEstado]);

    // Sondeo periódico (cada 5 min) para que el aviso de jornada salte aunque el
    // profesional esté inactivo en la interfaz.
    useEffect(() => {
        const t = setInterval(fetchEstado, 5 * 60 * 1000);
        return () => clearInterval(t);
    }, [fetchEstado]);

    useEffect(() => {
        if (fichaje && fichaje.estado === 'abierto') {
            intervalRef.current = setInterval(() => setSegundos(p => p + 1), 1000);
        } else {
            clearInterval(intervalRef.current);
        }
        return () => clearInterval(intervalRef.current);
    }, [fichaje]);

    const handleFichar = async (tipo) => {
        setProcesando(true);
        try {
            const res = await api.post(`/fichajes/${tipo}`);
            const data = res?.data || res;
            // ⚖️ Si el backend devuelve un aviso (p. ej. descanso insuficiente), lo mostramos.
            if (tipo === 'entrada' && data?.aviso) {
                showToast(data.aviso, 'warning');
            } else {
                showToast(`Jornada ${tipo === 'entrada' ? 'iniciada' : 'finalizada'}.`, 'success');
            }
            await fetchEstado();
        } catch (error) {
            showToast(error.message || error.response?.data?.message || 'Error', 'error');
        } finally {
            setProcesando(false);
        }
    };

    const formatTime = (totalS) => {
        const h = Math.floor(totalS / 3600);
        const m = Math.floor((totalS % 3600) / 60);
        const s = totalS % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    return {
        isActivo: !!fichaje,
        segundos,
        procesando,
        handleFichar,
        formatTime
    };
}